import { useState } from "react";
import { toast } from "sonner";
import {
    Rocket, Sparkles, Factory, GitCompare, ClipboardList, FileStack, FileDown,
    Boxes, Coins, Clock, Gauge, TrendingUp, ShieldAlert, ArrowRight,
} from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const COMMODITIES = [
    "Gold", "Copper", "PGM", "Coal", "Iron Ore",
    "Lithium", "Nickel", "Cobalt", "Green Hydrogen",
    "Water Treatment", "Fertiliser",
];
const PROCESSES = {
    "Gold":            ["CIP", "CIL", "Elution", "Gravity + CIL"],
    "Copper":          ["Concentrator", "SX-EW", "Leach + SX"],
    "PGM":             ["Concentrator", "Smelter", "Base Metal Refinery"],
    "Coal":            ["Beneficiation", "DMS", "Fine coal recovery"],
    "Iron Ore":        ["Beneficiation", "Pellet Plant", "DRI"],
    "Lithium":         ["Spodumene concentrator", "Brine SX", "Hydroxide refinery"],
    "Nickel":          ["Sulfide concentrator", "HPAL", "Ferronickel"],
    "Cobalt":          ["SX-EW", "Cobalt hydroxide", "Refinery"],
    "Green Hydrogen":  ["Electrolysis + Storage", "Ammonia synthesis"],
    "Water Treatment": ["Desalination (RO)", "Reuse & recycle", "Brine concentration"],
    "Fertiliser":      ["Ammonia", "Urea", "NPK"],
};

const CLIENTS = [
    "Sibanye-Stillwater", "Anglo American", "Kumba Iron Ore", "Harmony Gold",
    "First Quantum", "Khoemacau Copper", "OCP Africa", "Hive Hydrogen",
    "Vale Moçambique", "ERG Africa",
];
const LOCATIONS = [
    "South Africa", "Mozambique", "Botswana", "DRC", "Zambia", "Namibia",
    "Ghana", "Australia", "Chile", "Peru",
];

export default function PlantBaselineGenerator() {
    const [cfg, setCfg] = useState({
        commodity: "Gold",
        capacity: "4 Mtpa",
        location: "South Africa",
        client: "Sibanye-Stillwater",
        process: "CIP",
    });
    const [pkg, setPkg] = useState(null);
    const [busy, setBusy] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const set = (k, v) => setCfg((c) => {
        const next = { ...c, [k]: v };
        if (k === "commodity") next.process = (PROCESSES[v] || ["Process"])[0];
        return next;
    });

    const generate = async () => {
        setBusy(true);
        setPkg(null);
        try {
            const { data } = await api.post("/edi/plant-baseline", cfg);
            setPkg(data);
            toast.success("Plant baseline generated");
        } catch {
            toast.error("Plant baseline generation failed");
        } finally {
            setBusy(false);
        }
    };

    const exportPdf = async () => {
        if (!pkg) return;
        setDownloading(true);
        try {
            const token = localStorage.getItem("sgsb.token");
            const resp = await fetch(`${API_BASE}/reports/export`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    title: `Executive Bid Pack — ${cfg.commodity} · ${cfg.process}`,
                    content: pkg.bid_pack_markdown,
                    fmt: "pdf",
                    confidentiality: "Executive",
                }),
            });
            if (!resp.ok) throw new Error(String(resp.status));
            const cd = resp.headers.get("Content-Disposition") || "";
            const match = cd.match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : "SGSBateman_Plant_Baseline.pdf";
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 3000);
            toast.success("Executive bid pack downloaded");
        } catch {
            toast.error("Export failed");
        } finally {
            setDownloading(false);
        }
    };

    const processOptions = PROCESSES[cfg.commodity] || ["Process"];

    return (
        <div className="space-y-6" data-testid={EDI.plantBaselineRoot}>
            {/* Configurator */}
            <div className="glass rounded-xl p-6 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 90% 10%, rgba(212,175,55,0.22), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(59,130,246,0.16), transparent 55%)",
                    }}
                />
                <div className="relative">
                    <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                        <Factory className="h-4 w-4" strokeWidth={1.5} />
                        <span className="kicker text-[10px]">Process Plant Baseline Generator™</span>
                    </div>
                    <h3 className="mt-1 font-display font-black text-white text-2xl tracking-tight">
                        From commodity to a complete plant baseline.
                    </h3>
                    <p className="text-slate-400 text-sm font-body mt-1 max-w-3xl">
                        Configure the opportunity — the platform assembles the design basis, mass balance,
                        equipment register, data sheets, PFDs, P&IDs, drawing register, procurement plan,
                        contracting plan, engineering estimate and executive bid pack in seconds.
                    </p>

                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                        {[
                            ["Commodity", "commodity", COMMODITIES, EDI.plantBaselineCommodity],
                            ["Recovery Process", "process", processOptions, EDI.plantBaselineProcess],
                            ["Capacity", "capacity", ["1 Mtpa","2 Mtpa","4 Mtpa","6 Mtpa","8 Mtpa","12 Mtpa","20 Mtpa"], EDI.plantBaselineCapacity],
                            ["Client", "client", CLIENTS, EDI.plantBaselineClient],
                            ["Location", "location", LOCATIONS, EDI.plantBaselineLocation],
                        ].map(([label, key, options, tid]) => (
                            <div key={key}>
                                <label className="kicker text-slate-500 text-[10px] mb-1 block">{label}</label>
                                <select
                                    data-testid={tid}
                                    value={cfg[key]}
                                    onChange={(e) => set(key, e.target.value)}
                                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 px-3 py-2 text-sm font-body text-slate-100 outline-none focus:border-[color:var(--sgsb-gold)]"
                                >
                                    {options.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                            </div>
                        ))}
                    </div>

                    <div className="mt-5 flex flex-wrap gap-3">
                        <button
                            data-testid={EDI.plantBaselineGenerate}
                            disabled={busy}
                            onClick={generate}
                            className="inline-flex items-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-5 py-2.5 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                        >
                            <Sparkles className="h-4 w-4" strokeWidth={2} />
                            {busy ? "Assembling plant baseline…" : "Generate plant baseline"}
                        </button>
                        {pkg && (
                            <button
                                data-testid={EDI.plantBaselineExport}
                                onClick={exportPdf}
                                disabled={downloading}
                                className="inline-flex items-center gap-2 rounded-md border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)] px-5 py-2.5 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold)]/10 disabled:opacity-60 transition-colors duration-200"
                            >
                                <FileDown className="h-4 w-4" strokeWidth={2} />
                                {downloading ? "Preparing…" : "Export Executive Bid Pack"}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {pkg && (
                <>
                    {/* Headline KPIs */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {[
                            { l: "Reference Basis",       v: `${pkg.design_basis.similarity_pct}%`, s: pkg.design_basis.reference, Icon: GitCompare },
                            { l: "Engineering Hours Saved",v: pkg.hours_saved.toLocaleString(),     s: "vs greenfield baseline",   Icon: Clock },
                            { l: "Cost Avoided",          v: pkg.cost_saved,                        s: "Blended $150/hr",           Icon: Coins },
                            { l: "Reuse %",               v: `${pkg.reuse_score.overall_pct}%`,     s: "Across engineering package",Icon: TrendingUp },
                            { l: "Confidence",            v: `${pkg.confidence}%`,                  s: "Board-ready baseline",      Icon: Gauge },
                        ].map(({ l, v, s, Icon }) => (
                            <div key={l} className="glass hover-lift rounded-xl p-5 relative overflow-hidden">
                                <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                                <Icon className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                <div className="mt-2 kicker text-slate-400 text-[10px]">{l}</div>
                                <div className="mt-2 font-display font-black text-white text-2xl tracking-tighter">{v}</div>
                                <div className="mt-1 text-[11px] text-slate-500 font-body">{s}</div>
                            </div>
                        ))}
                    </div>

                    {/* Design basis + mass balance */}
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                        <div className="glass rounded-xl overflow-hidden xl:col-span-1">
                            <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                                <ClipboardList className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                <h3 className="font-display font-black text-white tracking-tight">Process Design Basis</h3>
                            </div>
                            <table className="w-full text-sm font-body">
                                <tbody>
                                    {[
                                        ["Commodity", pkg.design_basis.commodity],
                                        ["Process", pkg.design_basis.process],
                                        ["Capacity", pkg.design_basis.capacity],
                                        ["Location", pkg.design_basis.location],
                                        ["Client", pkg.design_basis.client],
                                        ["Recovery", pkg.design_basis.recovery],
                                        ["Availability", pkg.design_basis.availability],
                                        ["Ambient", pkg.design_basis.ambient],
                                        ["Elevation", pkg.design_basis.elevation],
                                        ["Seismic", pkg.design_basis.seismic],
                                    ].map(([k, v], i) => (
                                        <tr key={k} className={i === 0 ? "" : "border-t border-slate-800/60"}>
                                            <td className="px-4 py-2 text-slate-500 text-xs uppercase tracking-wider w-40">{k}</td>
                                            <td className="px-2 py-2 text-slate-100">{v}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="glass rounded-xl overflow-hidden xl:col-span-2">
                            <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                                <FileStack className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                <h3 className="font-display font-black text-white tracking-tight">Mass Balance Snapshot</h3>
                                <div className="flex-1" />
                                <span className="kicker text-[10px] text-slate-500">{pkg.mass_balance.length} streams</span>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm font-body">
                                    <thead>
                                        <tr className="text-slate-500 kicker text-[10px]">
                                            <th className="text-left px-4 py-3">Stream</th>
                                            <th className="text-left px-4 py-3">Description</th>
                                            <th className="text-left px-4 py-3">Phase</th>
                                            <th className="text-right px-4 py-3">t/h</th>
                                            <th className="text-right px-4 py-3">Solids %</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pkg.mass_balance.map((s) => (
                                            <tr key={s.stream} className="border-t border-slate-800/60">
                                                <td className="px-4 py-2 text-slate-100">{s.stream}</td>
                                                <td className="px-4 py-2 text-slate-300">{s.description}</td>
                                                <td className="px-4 py-2 text-slate-400">{s.phase}</td>
                                                <td className="px-4 py-2 text-right text-slate-200">{s.t_h}</td>
                                                <td className="px-4 py-2 text-right text-slate-400">{s.solids_pct}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Equipment register */}
                    <div className="glass rounded-xl overflow-hidden">
                        <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                            <Boxes className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                            <h3 className="font-display font-black text-white tracking-tight">Equipment Register</h3>
                            <div className="flex-1" />
                            <span className="kicker text-[10px] text-slate-500">{pkg.equipment_register.length} items</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm font-body">
                                <thead>
                                    <tr className="text-slate-500 kicker text-[10px]">
                                        <th className="text-left px-4 py-3">Tag</th>
                                        <th className="text-left px-4 py-3">Description</th>
                                        <th className="text-left px-4 py-3">Duty</th>
                                        <th className="text-left px-4 py-3">Material</th>
                                        <th className="text-left px-4 py-3">Preferred Vendor</th>
                                        <th className="text-right px-4 py-3">Reused</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pkg.equipment_register.map((e) => (
                                        <tr key={e.tag} className="border-t border-slate-800/60">
                                            <td className="px-4 py-2 text-slate-100 font-medium">{e.tag}</td>
                                            <td className="px-4 py-2 text-slate-300">{e.description}</td>
                                            <td className="px-4 py-2 text-slate-400">{e.duty}</td>
                                            <td className="px-4 py-2 text-slate-400">{e.material}</td>
                                            <td className="px-4 py-2 text-slate-300">{e.vendor}</td>
                                            <td className="px-4 py-2 text-right">
                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 px-2 py-0.5 text-[10px] text-emerald-400">
                                                    Reused
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Drawing register + Procurement + COP */}
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                        <div className="glass rounded-xl overflow-hidden">
                            <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                                <FileStack className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                <h3 className="font-display font-black text-white tracking-tight">Drawing Register</h3>
                                <div className="flex-1" />
                                <span className="kicker text-[10px] text-slate-500">{pkg.drawing_register.total} drawings</span>
                            </div>
                            <table className="w-full text-sm font-body">
                                <thead>
                                    <tr className="text-slate-500 kicker text-[10px]">
                                        <th className="text-left px-4 py-3">Discipline</th>
                                        <th className="text-right px-4 py-3">Total</th>
                                        <th className="text-right px-4 py-3">Reused</th>
                                        <th className="text-right px-4 py-3">New</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pkg.drawing_register.by_discipline.map((d) => (
                                        <tr key={d.discipline} className="border-t border-slate-800/60">
                                            <td className="px-4 py-2 text-slate-100">{d.discipline}</td>
                                            <td className="px-4 py-2 text-right text-slate-200">{d.total}</td>
                                            <td className="px-4 py-2 text-right text-emerald-400 font-display font-bold">{d.reused}</td>
                                            <td className="px-4 py-2 text-right text-slate-400">{d.new}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="glass rounded-xl overflow-hidden">
                            <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                                <ClipboardList className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                <h3 className="font-display font-black text-white tracking-tight">Procurement Operating Plan</h3>
                                <div className="flex-1" />
                                <span className="kicker text-[10px] text-slate-500">{pkg.pop.total_value}</span>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm font-body">
                                    <thead>
                                        <tr className="text-slate-500 kicker text-[10px]">
                                            <th className="text-left px-4 py-3">Package</th>
                                            <th className="text-right px-4 py-3">Value</th>
                                            <th className="text-left px-4 py-3">Strategy</th>
                                            <th className="text-right px-4 py-3">Wks</th>
                                            <th className="text-right px-4 py-3">Risk</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pkg.procurement_packages.map((p) => (
                                            <tr key={p.package} className="border-t border-slate-800/60">
                                                <td className="px-4 py-2 text-slate-100">{p.package}</td>
                                                <td className="px-4 py-2 text-right text-slate-200">{p.value}</td>
                                                <td className="px-4 py-2 text-slate-400">{p.strategy}</td>
                                                <td className="px-4 py-2 text-right text-slate-400">{p.timeline.replace(/\swks/, "")}</td>
                                                <td className="px-4 py-2 text-right">
                                                    <span className={`rounded-full px-2 py-0.5 text-[10px] border ${p.risk === "High" ? "border-red-500/40 text-red-400 bg-red-500/10" : p.risk === "Medium" ? "border-amber-500/40 text-amber-400 bg-amber-500/10" : "border-emerald-500/40 text-emerald-400 bg-emerald-500/10"}`}>
                                                        {p.risk}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="glass rounded-xl p-6">
                            <div className="flex items-center gap-2">
                                <FileStack className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                <h3 className="font-display font-black text-white tracking-tight">Contracting Operating Plan</h3>
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-3">
                                {[
                                    ["Contracts",  pkg.cop.contracts],
                                    ["Exposure",   pkg.cop.exposure],
                                    ["Open claims",pkg.cop.claims_open],
                                    ["Avg award",  `${pkg.cop.avg_award_d} d`],
                                ].map(([k, v]) => (
                                    <div key={k} className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                                        <div className="kicker text-slate-500 text-[10px]">{k}</div>
                                        <div className="mt-1 font-display font-black text-white text-lg tracking-tight">{v}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4">
                                <div className="kicker text-slate-500 text-[10px] mb-2">Strategy Mix</div>
                                {pkg.cop.strategy_mix.map((s) => (
                                    <div key={s.type} className="flex items-center gap-3 py-1.5">
                                        <div className="text-sm text-slate-300 font-body w-40">{s.type}</div>
                                        <div className="flex-1 h-2 rounded-full bg-slate-800 overflow-hidden">
                                            <div className="h-full bg-[var(--sgsb-gold)]" style={{ width: `${(s.count / pkg.cop.contracts) * 100}%` }} />
                                        </div>
                                        <div className="text-xs text-slate-400 w-8 text-right">{s.count}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Engineering deliverables + execution + estimate */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                        <div className="glass rounded-xl overflow-hidden">
                            <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                                <ClipboardList className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                <h3 className="font-display font-black text-white tracking-tight">Engineering Deliverables</h3>
                            </div>
                            <table className="w-full text-sm font-body">
                                <thead>
                                    <tr className="text-slate-500 kicker text-[10px]">
                                        <th className="text-left px-4 py-3">Discipline</th>
                                        <th className="text-right px-4 py-3">Deliverables</th>
                                        <th className="text-right px-4 py-3">Auto %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pkg.engineering_deliverables.map((d) => (
                                        <tr key={d.discipline} className="border-t border-slate-800/60">
                                            <td className="px-4 py-2 text-slate-100">{d.discipline}</td>
                                            <td className="px-4 py-2 text-right text-slate-200">{d.deliverables}</td>
                                            <td className="px-4 py-2 text-right">
                                                <div className="inline-flex items-center gap-2">
                                                    <div className="h-1.5 w-24 rounded-full bg-slate-800 overflow-hidden">
                                                        <div className="h-full bg-[var(--sgsb-gold)]" style={{ width: `${d.auto_pct}%` }} />
                                                    </div>
                                                    <span className="text-[var(--sgsb-gold)] font-display font-bold">{d.auto_pct}%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="glass rounded-xl p-6">
                            <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                                <Rocket className="h-4 w-4" strokeWidth={1.5} />
                                <span className="kicker text-[10px]">Execution Strategy</span>
                            </div>
                            <h3 className="mt-1 font-display font-black text-white text-lg tracking-tight">
                                {pkg.execution_strategy.duration_months} month execution baseline
                            </h3>
                            <div className="mt-4 space-y-2">
                                {pkg.execution_strategy.milestones.map((m) => (
                                    <div key={m.m} className="flex items-center gap-3">
                                        <div className="text-xs text-slate-500 w-16">{m.date}</div>
                                        <div className="flex-1 flex items-center gap-3">
                                            <div className="text-sm text-slate-100 font-body flex-1">{m.m}</div>
                                            <span className="text-[11px] text-[var(--sgsb-gold)] font-display font-bold">{m.reuse_pct}% reuse</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-5 grid grid-cols-2 gap-3">
                                {[
                                    ["Hours baseline", pkg.engineering_estimate.hours_baseline.toLocaleString()],
                                    ["Hours saved",    `${pkg.engineering_estimate.hours_saved.toLocaleString()}`],
                                    ["Net hours",      pkg.engineering_estimate.hours_net.toLocaleString()],
                                    ["Contingency",    `${pkg.engineering_estimate.contingency_pct}%`],
                                    ["Productivity",   `${pkg.engineering_estimate.productivity_idx}×`],
                                    ["Confidence",     `${pkg.engineering_estimate.confidence_pct}%`],
                                ].map(([k, v]) => (
                                    <div key={k} className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                                        <div className="kicker text-slate-500 text-[10px]">{k}</div>
                                        <div className="mt-1 font-display font-black text-white text-lg tracking-tight">{v}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Lessons + Reuse split */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                        <div className="glass rounded-xl overflow-hidden">
                            <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                                <ShieldAlert className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                <h3 className="font-display font-black text-white tracking-tight">Applied Lessons Learned</h3>
                            </div>
                            <div className="divide-y divide-slate-800/60">
                                {pkg.lessons_learned.map((l) => (
                                    <div key={l.ref} className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <span className="kicker text-slate-500 text-[10px]">{l.ref}</span>
                                            <span className="rounded-full bg-slate-800/60 border border-slate-700 px-2 py-0.5 text-[10px] text-slate-300">{l.category}</span>
                                            <span className="ml-auto text-[var(--sgsb-gold)] font-display font-bold text-sm">{l.relevance}%</span>
                                        </div>
                                        <div className="mt-1 text-sm text-slate-200 font-body">{l.recommendation}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="glass rounded-xl p-6">
                            <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                                <TrendingUp className="h-4 w-4" strokeWidth={1.5} />
                                <span className="kicker text-[10px]">Knowledge Reuse Score</span>
                            </div>
                            <h3 className="mt-1 font-display font-black text-white text-2xl tracking-tighter">
                                {pkg.reuse_score.overall_pct}% overall reuse
                            </h3>
                            <div className="mt-4 space-y-2">
                                {pkg.reuse_score.by_category.map((c) => (
                                    <div key={c.category} className="flex items-center gap-3">
                                        <div className="text-sm text-slate-300 font-body w-40">{c.category}</div>
                                        <div className="flex-1 h-2 rounded-full bg-slate-800 overflow-hidden">
                                            <div className="h-full bg-[var(--sgsb-gold)]" style={{ width: `${c.pct}%` }} />
                                        </div>
                                        <div className="text-xs text-[var(--sgsb-gold)] font-display font-bold w-10 text-right">{c.pct}%</div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-5 rounded-md border border-[color:var(--sgsb-gold)]/30 bg-[color:var(--sgsb-gold)]/5 p-4 text-sm font-body">
                                <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                                    <Rocket className="h-4 w-4" strokeWidth={1.5} />
                                    <span className="kicker text-[10px]">Executive summary</span>
                                </div>
                                <p className="mt-2 text-slate-200">
                                    A complete plant baseline — engineering, procurement, contracting and
                                    executive — is assembled for the {cfg.commodity} · {cfg.process} opportunity
                                    at {cfg.capacity} in {cfg.location} for {cfg.client}.
                                </p>
                                <p className="mt-2 flex items-center gap-1 text-[var(--sgsb-gold)] font-display font-bold">
                                    <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                                    Estimated engineering saving: {pkg.cost_saved} · Confidence: {pkg.confidence}%
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
